import { Autocomplete, Box, CircularProgress, InputAdornment, Skeleton, TextField, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CoinWithSymbol, Transaction } from '../../../../../services/transaction.types';
import { UserDetails } from '../../../../../services/user.types';
import { filterOptionsWithCreate } from '../../../../../utils/autocomplete';
import CoinAutocomplete from './CoinAutocomplete';

interface BuySectionProps {
    formValues: Partial<Transaction>;
    currentCtUser: UserDetails | null;
    onAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onPriceChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onValueChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onCoinChange: (value: CoinWithSymbol | null | string) => void;
    onPoolChange: (value: string | null) => void;
    liquidityPools: string[];
    hasMissingPrices?: boolean;
    formatInput: (field: string, value: number | undefined) => string;
    showLiquidityPool?: boolean;
    isUpdatingPrices?: boolean;
    isLiquidityPoolLoading?: boolean;
}

const BuySection: React.FC<BuySectionProps> = ({
    formatInput,
    formValues,
    currentCtUser,
    onAmountChange,
    onPriceChange,
    onValueChange,
    onCoinChange,
    onPoolChange,
    hasMissingPrices,
    showLiquidityPool,
    liquidityPools,
    isUpdatingPrices,
    isLiquidityPoolLoading,
}) => {
    const { t } = useTranslation();
    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                {t('transactions.modal.form.incoming')}
            </Typography>
            <Box sx={{ width: '100%' }}>
                <Box sx={{ 
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: '1fr 1fr',
                        md: '1fr 1fr 1fr 1fr'
                    },
                    gap: 2,
                    mb: 2
                }}>
                    <TextField
                        fullWidth
                        label={t('transactions.modal.form.buy.buyAmount')}
                        type="text"
                        inputProps={{
                            inputMode: 'decimal',
                            pattern: '[0-9]*',
                        }}
                        value={formatInput('buy', formValues.buy)}
                        onChange={onAmountChange}
                        sx={{
                            '& .MuiInputBase-root': { borderRadius: 1 },
                        }}
                        InputProps={{
                            inputProps: { min: 0, step: '0.00000001' },
                        }}
                    />
                    <CoinAutocomplete
                        value={formValues.buyCoin || ''}
                        displayValue={formValues.buyTicker || formValues.buyCoin || ''}
                        label={t('transactions.modal.form.buy.buyCoin')}
                        onCoinChange={onCoinChange}
                    />
                    <TextField
                        fullWidth
                        label={t('transactions.modal.form.pricePerUnit')}
                        type="text"
                        error={hasMissingPrices && !formValues.buyPricePerUnit}
                        helperText={hasMissingPrices && !formValues.buyPricePerUnit ? t('transactions.modal.form.buy.priceRequired') : undefined}
                        inputProps={{
                            inputMode: 'decimal',
                            pattern: '[0-9]*',
                        }}
                        value={formatInput('buyPricePerUnit', formValues.buyPricePerUnit)}
                        onChange={onPriceChange}
                        sx={{
                            '& .MuiInputBase-root': { borderRadius: 1 },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    {isUpdatingPrices ? <CircularProgress size={16} /> : currentCtUser?.currency || ''}
                                </InputAdornment>
                            ),
                            inputProps: { min: 0, step: '0.00000001' },
                        }}
                    />
                    <TextField
                        fullWidth
                        label={t('transactions.modal.form.buy.buyValueInFiat')}
                        type="text"
                        inputProps={{
                            inputMode: 'decimal',
                            pattern: '[0-9]*',
                        }}
                        value={formatInput('buyValueInFiat', formValues.buyValueInFiat)}
                        onChange={onValueChange}
                        sx={{
                            '& .MuiInputBase-root': { borderRadius: 1 },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    {currentCtUser?.currency || ''}
                                </InputAdornment>
                            ),
                            inputProps: { min: 0, step: '0.00000001' },
                        }}
                    />
                </Box>
                {showLiquidityPool && isLiquidityPoolLoading && (
                    <Box sx={{ mt: 2 }}>
                        <Skeleton variant="rectangular" height={56} width="100%" animation="wave" />
                    </Box>
                )}
                {showLiquidityPool && !isLiquidityPoolLoading && (
                    <Box sx={{ mt: 2 }}>
                        <Autocomplete
                            value={formValues.buyPoolName || null}
                            onChange={(_, value) => onPoolChange(value)}
                            options={liquidityPools}
                            disablePortal
                            freeSolo
                            autoSelect
                            filterOptions={filterOptionsWithCreate}
                            renderOption={(props, option) => {
                                const { key, ...otherProps } = props;
                                return (
                                    <li key={key} {...otherProps}>
                                        {option}
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('transactions.modal.form.buy.liquidityPool')}
                                    fullWidth
                                    sx={{
                                        '& .MuiInputBase-root': { borderRadius: 1 },
                                    }}
                                />
                            )}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default BuySection;
